import type { GraphClearJobStatus } from './api';
import { describeApiError } from './apiErrors';

/** The rules the derivation pass after a clear could not run; empty when it ran them all. */
function rulesLine(rules: { name: string; problem: string }[]): string {
  if (rules.length === 0) return '';
  const named = rules.map((r) => `${r.name} (${r.problem})`).join('; ');
  return rules.length === 1
    ? ` Derived facts: rule ${named} does not run and was skipped; edit it or turn it off on the Rules page.`
    : ` Derived facts: ${rules.length} rules do not run and were skipped, ${named}; edit them or turn them off on the Rules page.`;
}

/** One line for a clear job on the Graphs tab (#113): queued and what it waits
 *  behind, clearing, cleared with its count, or failed with the server's reason. */
/** Prefixes named in a clear's line before the rest are counted. */
const PREFIXES_NAMED = 8;

/** What a clear did to the Namespaces list; empty when it removed no prefix. */
function prefixesLine(prefixes: string[]): string {
  if (prefixes.length === 0) return '';
  const named = prefixes.slice(0, PREFIXES_NAMED).join(', ');
  const more = prefixes.length - PREFIXES_NAMED;
  const list = more > 0 ? `${named} and ${more} more` : named;
  return prefixes.length === 1
    ? ` The prefix ${list} came with that data and nothing uses it now, so it left the Namespaces list.`
    : ` ${prefixes.length} prefixes came with that data and nothing uses them now, so they left the Namespaces list: ${list}.`;
}

export function clearJobLine(job: GraphClearJobStatus): string {
  const line = clearJobOutcome(job);
  return job.progress.restarted
    ? `${line} The site restarted while this clear waited, and took it up again when it started.`
    : line;
}

function clearJobOutcome(job: GraphClearJobStatus): string {
  const graphs = job.graphs.join(', ');
  if (job.state === 'running') {
    if (job.progress.stage === 'queued') {
      return job.progress.behind
        ? `Queued: ${graphs}, behind ${job.progress.behind}`
        : `Queued: ${graphs}`;
    }
    return `Clearing ${graphs} (${Math.round(job.elapsed_ms / 1000)}s)`;
  }
  if (job.state === 'done') {
    const removed = (job.result ?? []).reduce((n, r) => n + (r.statements_removed ?? 0), 0);
    const cleared =
      removed > 0
        ? `Cleared ${graphs}: ${removed.toLocaleString()} statement(s) removed.`
        : `Cleared ${graphs}.`;
    return `${cleared}${prefixesLine(job.progress.prefixes_removed ?? [])} The index rebuilds once the clears settle.${rulesLine(job.progress.rules_skipped ?? [])}`;
  }
  return `${graphs} was not cleared. ${describeApiError(job.http_status ?? 500, job.error, job.detail)}`;
}
